/// <amd-module name="Dashboards/DashboardView/src/redux/reducers/Reducer" />
import * as Resources from "Dashboards/DashboardView/Resources";
import { DashboardWidgetProps } from "Dashboards/WidgetContracts/Internal/DashboardWidgetProps";
import produce from "immer";
import { TelemetryActionTypes as VssTelemetryActionTypes } from "VSS/Redux/Modules/Telemetry";
import { DashboardViewState, WriteableDashboardViewState } from "../../model/DashboardViewState";
import { BoundingRectangle, Viewport } from "../../model/Viewport";
import { WidgetMap } from "../../model/WidgetMap";
import { isRectanglePartiallyVisibleWithinViewport } from "../../utilities/Viewport";
import { DashboardAction, DashboardActionTypes } from "../actions/Actions";
import { ErrorAction, ErrorActionTypes } from "../actions/ErrorActions";
import { OmittedTTIScenarioName, TelemetryAction } from "../actions/TelemetryActions";
import * as Selectors from "../selectors/ReducerSelectors";

const defaultState: DashboardViewState = {
    dashboard: undefined,
    pastWidgets: [],
    widgets: [],
    showDashboardTemplatePage: true,
    previewWidgets: undefined,
    dashboardIsInitialized: false,
    showEditMode: false,
    embedded: false,
    showConfigMode: false,
    previewWidgetId: undefined,
    highlightOnPreview: true,
    showDashboardSettingsManager: false,
    showCopyDashboardPanel: false,
    widgetsSeen: [],
    widgetsSeenInitially: [],
    widgetsLoaded: [],
    errorState: undefined,
    renderTimestamp: 0,
    currentTimestamp: 0,
    showRefreshTime: false,
    teamData: undefined,
    ownerIdentity: undefined,
    defaultProjectTeamData: undefined,
    permissionsState: {
        canCreateDashboards: false,
        canEditDashboard: false,
        canManagePermissionsForDashboard: false
    },
    showFullScreen: false,
    publishedDirtyTTI: false,
    isThrottlingWarningActive: false,
    isThrottlingAllowed: false,
    omitTenPercentVisibleWidgetsFromRender: false,
    copyDashboardPanelState: {
        useDedicatedApi: false,
        projects : [],
        projectTeamList: {}
    }
};

export function dashboardViewStateReducer(state: DashboardViewState = defaultState, action: DashboardAction | TelemetryAction | ErrorAction) {
    return produce(state, (draft: WriteableDashboardViewState) => {
        switch (action.type) {
            case DashboardActionTypes.CopyWidgetToThisDashboard: {
                const settings: DashboardWidgetProps = action.payload.initialWidgetSettings;
                const widgetId = getNextClientWidgetId();

                // Create new Widget props based on source.
                const widget: DashboardWidgetProps = {
                    ...settings,
                    id: widgetId,
                    position: {
                        row: 0,
                        column: 0
                    }
                };

                draft.widgets.push(widget);
                draft.widgetsSeen.push(widgetId);
                break;
            }

            case DashboardActionTypes.DropWidgetFromCatalog: {
                const settings: DashboardWidgetProps = action.payload.initialWidgetSettings;
                const widgetId = getNextClientWidgetId();

                // Create new Widget props based on source.
                // Position will be updated by react-grid-layout, which takes the below dragEvent and moves the widget
                const widget: DashboardWidgetProps = {
                    ...settings,
                    id: widgetId,
                    position: {
                        row: 0,
                        column: 0
                    }
                };

                draft.dragEvent = action.payload.dragEvent;
                draft.draggedWidgetId = widgetId;

                draft.widgets.push(widget);
                draft.widgetsSeen.push(widgetId);
                break;
            }

            case DashboardActionTypes.DeleteWidget: {
                const id = action.payload.widgetProps.id;
                draft.widgets = state.widgets.filter(item => {
                    return item.id !== id;
                });
                break;
            }

            case DashboardActionTypes.SavePreviewWidgetConfig: {
                // Note: This step is needed only for legacy compatability. A widget config could fail to notify of the final state before a save.
                const widgetToUpdate = draft.previewWidgets!.find(item => {
                    return item.id === draft.previewWidgetId;
                })!;
                updateConfigurableWidgetFields(widgetToUpdate, action.payload.previewWidgetConfig);

                // Transfer widgets preview state to widgets, as the user is satisfied with the config changes. Return to prior state.
                draft.widgets = draft.previewWidgets!;
                draft.showConfigMode = false;
                draft.previewWidgetId = undefined;
                draft.previewWidgets = undefined;
                break;
            }

            case DashboardActionTypes.UpdatePreviewWidgetConfig: {
                const widgetToUpdate = draft.previewWidgets!.find(item => {
                    return item.id === draft.previewWidgetId;
                })!;
                updateConfigurableWidgetFields(widgetToUpdate, action.payload.previewWidgetConfig);

                break;
            }

            case DashboardActionTypes.TogglePreviewHighlighting: {
                draft.highlightOnPreview = action.payload.highlightPreviewConfig;

                break;
            }

            case DashboardActionTypes.NotifyWidgetsMoved: {
                // Update state to reflect updated position of each widget as described in grid layout
                const draftWidgets = state.previewWidgets ? draft.previewWidgets : draft.widgets;
                const stateWidgets = state.previewWidgets ? state.previewWidgets : state.widgets;
                let unpositionedWidgetWasPlaced = false;
                for (const newPosition of action.payload.widgetPositions) {
                    const index = stateWidgets.findIndex(w => w.id === newPosition.id.substr(-36, 36));
                    /*
                     * If the widget went from position (0, 0) to somewhere else, this had to have been done automatically
                     * on behalf of the user. This happens the first time a dashboard is viewed after a widget has been pinned.
                     */
                    if (
                        !unpositionedWidgetWasPlaced &&
                        stateWidgets[index].position.column === 0 &&
                        stateWidgets[index].position.row === 0 &&
                        newPosition.column !== 0 &&
                        newPosition.row !== 0
                    ) {
                        unpositionedWidgetWasPlaced = true;
                    }
                    draft.unpositionedWidgetWasPlaced = unpositionedWidgetWasPlaced;
                    if (index >= 0) {
                        draftWidgets![index].position.column = newPosition.column;
                        draftWidgets![index].position.row = newPosition.row;
                    } else {
                        throw new Error("Unrecognized Widget id encountered during layout update.");
                    }
                }

                break;
            }

            case DashboardActionTypes.UnloadDashboard:
                // Forces render pass using default values to unmount the widgets prior
                // to other actions loading new state. This simulates a refresh of all the widgets.
                return {
                    ...defaultState,

                    // List of run-time fields that shouldn't be unloaded during refresh
                    teamData: state.teamData,
                    defaultProjectTeamData: state.defaultProjectTeamData,
                    ownerIdentity: state.ownerIdentity,
                    showFullScreen: state.showFullScreen,
                    isThrottlingWarningActive: state.isThrottlingWarningActive,
                    isThrottlingAllowed: state.isThrottlingAllowed,

                    // We also exclude permissions from unload because we don't fetch them again
                    permissionsState: state.permissionsState,

                    // Once we publish TTI, we don't want to publish it again
                    publishedDirtyTTI: state.publishedDirtyTTI
                };
                break;

            case DashboardActionTypes.SetDashboard:
                draft.dashboard = action.payload.dashboard;
                draft.renderTimestamp = Date.now();
                break;

            case DashboardActionTypes.SetWidgets:
                draft.widgets = action.payload.widgets as DashboardWidgetProps[];
                break;

            case DashboardActionTypes.SetPastWidgets:
                draft.pastWidgets = action.payload.pastWidgets as DashboardWidgetProps[];
                break;

            case DashboardActionTypes.UpdateWidgetEtag:
                action.payload.widgets.forEach(incomingWidget => {
                    draft.widgets.forEach(existingWidget => {
                        if (incomingWidget.id === existingWidget.id)
                            existingWidget.eTag = incomingWidget.eTag;
                    });
                });
                break;

            case DashboardActionTypes.SetDashboardAndWidgets:
                draft.dashboard = action.payload.dashboard;
                draft.renderTimestamp = Date.now();
                draft.widgets = action.payload.widgets as DashboardWidgetProps[];
                draft.pastWidgets = action.payload.pastWidgets as DashboardWidgetProps[];
                break;

            case DashboardActionTypes.UpdateRefreshTimer:
                draft.currentTimestamp = Date.now();
                break;

            case DashboardActionTypes.ToggleRefreshTimeDisplay:
                draft.showRefreshTime = action.payload.showRefreshTimeDisplay;
                break;

            case DashboardActionTypes.SetTeamData:
                draft.teamData = action.payload.teamData;
                break;

            case DashboardActionTypes.SetOwnerIdentity:
                draft.ownerIdentity = action.payload.ownerIdentity;
                break;

            case DashboardActionTypes.SetDefaultProjectTeamData:
                const payloadProjectTeamData = action.payload.defaultProjectTeamData;
                draft.defaultProjectTeamData = {
                    id: payloadProjectTeamData.id,
                    name: payloadProjectTeamData.name
                };
                break;

            case DashboardActionTypes.SetEmbedded:
                draft.embedded = action.payload.isEmbedded;
                break;

            case DashboardActionTypes.ToggleEditMode:
                draft.showEditMode = action.payload.showEditMode;
                break;

            case DashboardActionTypes.hideDashboardTemplatePage: {
                draft.showDashboardTemplatePage = false;
                break;
            }

            case DashboardActionTypes.ToggleConfigMode:
                draft.showConfigMode = action.payload.showConfigMode;
                draft.previewWidgetId = draft.showConfigMode ? action.payload.selectedWidgetId : undefined;
                draft.previewWidgets = draft.showConfigMode ? draft.widgets : undefined;
                break;

            case DashboardActionTypes.SetPermissions:
                draft.permissionsState = action.payload.permissions;
                break;

            case DashboardActionTypes.NotifyWidgetLoaded:
                draft.widgetsLoaded.push(action.payload.eventInfo.widget.id!);
                break;

            case DashboardActionTypes.NotifyDashboardInitialized:
                draft.dashboardIsInitialized = true;
                break;

            case DashboardActionTypes.UpdateWidgetsSeen:
                const { gridViewport, gridItemRectangles } = action.payload;
                updateWidgetsSeenState(gridViewport, gridItemRectangles, state, draft);
                break;

            case DashboardActionTypes.ToggleFullScreen:
                draft.showFullScreen = action.payload.showFullScreen;
                break;

            case VssTelemetryActionTypes.EndScenario:
                if (action.payload.scenarioName === OmittedTTIScenarioName) {
                    draft.publishedDirtyTTI = true;
                }
                break;

            case DashboardActionTypes.ToggleDashboardSettingsManager:
                draft.showDashboardSettingsManager = action.payload.showDashboardSettingsManager;
                break;

            case DashboardActionTypes.UpdateDashboardSettings:
                draft.dashboard!.name = action.payload.dashboardName;
                draft.dashboard!.description = action.payload.dashboardDescription;
                draft.dashboard!.refreshInterval = action.payload.dashboardRefreshInterval;
                draft.dashboard!.ownerId = action.payload.dashboardOwner;
                break;

            case ErrorActionTypes.DismissErrorMessage:
                draft.errorState = undefined;
                break;

            case ErrorActionTypes.NotifySagaFailure:
                draft.errorState = {
                    errorMessage: action.payload.message,
                    showRefreshLink: false
                };
                break;

            case ErrorActionTypes.NotifyDashboardLoadFailure:
                draft.errorState = {
                    errorMessage: Resources.Error_DashboardLoadFailed,
                    showRefreshLink: true
                };
                break;

            case ErrorActionTypes.NotifyDashboardSaveFailure:
                draft.errorState = {
                    errorMessage: Resources.Error_DashboardSaveFailed,
                    showRefreshLink: true
                };

                if (action.payload.message && action.payload.message.includes("VS402427")) {
                    draft.errorState.errorMessage = Resources.Error_WidgetMaxCountExceeded;
                } else if ((action.payload as any).status === 409) {
                    draft.errorState.errorMessage = Resources.Error_DashboardOutOfSync;
                }

                break;

            case DashboardActionTypes.SetAllowThrottling:
                draft.isThrottlingAllowed = action.payload.allowThrottling;
                break;

            case DashboardActionTypes.SetThrottlingWarning:
                draft.isThrottlingWarningActive = action.payload.showThrottlingWarning;
                break;

            case DashboardActionTypes.DismissThrottlingWarning:
                draft.isThrottlingWarningActive = false;
                break;

            case DashboardActionTypes.SetOmitTenPercentVisibleWidgets:
                draft.omitTenPercentVisibleWidgetsFromRender = action.payload.omitTenPercentVisibleWidgetsFromRender;
                break;
            case DashboardActionTypes.ToggleCopyDashboardPanel:
                draft.showCopyDashboardPanel = action.payload.showCopyDashboardPanel;
                break;
            case DashboardActionTypes.InitializeStateForCopyDashboard:
                draft.showCopyDashboardPanel = true;
                draft.copyDashboardPanelState.useDedicatedApi = action.payload.useDedicatedApi;
                draft.copyDashboardPanelState.projects = action.payload.projectList;
                draft.copyDashboardPanelState.projectTeamList = action.payload.teamProjectMap;
                break;
            case DashboardActionTypes.PutNewTeamsForProject:
                draft.copyDashboardPanelState.projectTeamList[action.payload.projectId] = action.payload.teamProjectItem;
                break;
        }
    });
}

function updateWidgetsSeenState(
    gridViewport: Viewport,
    gridItemRectangles: WidgetMap<BoundingRectangle>,
    state: DashboardViewState,
    draftState: WriteableDashboardViewState
) {
    const widgetsSeenMap = Selectors.getWidgetsSeenMap(state);
    for (const widgetId in gridItemRectangles) {
        if (!widgetsSeenMap[widgetId]) {
            const visibleInViewport = isRectanglePartiallyVisibleWithinViewport(
                gridItemRectangles[widgetId],
                gridViewport,
                state.omitTenPercentVisibleWidgetsFromRender
            );
            if (visibleInViewport) {
                draftState.widgetsSeen.push(widgetId);

                const dashboardIsInitialized = Selectors.dashboardIsInitialized(state);
                if (!dashboardIsInitialized) {
                    draftState.widgetsSeenInitially.push(widgetId);
                }
            }
        }
    }
}

let addedCount = 1;

function getNextClientWidgetId(): string {
    // Generates a GUID-sized client string, which is definitely not a guid, and will be rejected if sent to server.
    const nextId = "LOCAL" + paddedInt(addedCount++, 31);
    return nextId;
}
/** Pads the supplied number with a series of zeros, as specified by maxLength */
function paddedInt(value: number, maxLength: number) {
    const pad = new Array(1 + maxLength).join("0");
    return (pad + value).slice(-pad.length);
}

/** Applies state for fields which are configurable. */
function updateConfigurableWidgetFields(widgetToUpdate: DashboardWidgetProps, updatedConfig: DashboardWidgetProps) {
    widgetToUpdate.name = updatedConfig.name;
    widgetToUpdate.size = updatedConfig.size;
    widgetToUpdate.settings = updatedConfig.settings;
    widgetToUpdate.settingsVersion = updatedConfig.settingsVersion;
}
